import React from "react";
import _ from "lodash";
import moment from "moment";
import classNames from "classnames";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import { colors } from "Constants";
import { Avatar } from "@toddle-edu/ds-web";
import {
  DownloadOutlined,
  PencilLineOutlined,
  UserTickOutlined,
  ShareCurveOutlined,
  CopyOutlined,
  SwitchArrowsOutlined,
  ReloadArrowOutlined,
  DeleteOutlined,
  LockFilled,
} from "@toddle-edu/ds-icons";
import ACLStore from "lib/aclStore";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getIsProSchool } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils";
import { ENABLE_ASSESSMENTS_IN_PYP } from "Constants/permissionConstants";
import { getIsGradingPeriodUnlocked } from "Login/routes/Administrator/commonComponents/GradingSetup/modules/Utils";
import HtmlParser from "react-html-parser";
import {
  convertHtmlToTextWithSpanConversion,
  getStyleStrippedText,
  getRethumbUrl,
  getMemoizedLangFunction,
  getThumbnailIcon,
  getThumbUrl,
} from "Utils";
import {
  getAssessmentTypeDropdownMemoized,
  getItemTypeOptionsMemoized,
} from "Utils/EntityFlowUtils";
import ResourceImagePlaceHolder from "UIComponents/ResourceImagePlaceHolder";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import { ATTENDANCE_GLOBAL_CONSTANTS } from "modules/Services";

// ============================================================================
// CONSTANTS
// ============================================================================
export const BREAK_ITEM_TYPE = "BREAK";

// Calendar view modes
export const CALENDAR_VIEW = {
  WEEKLY: "WEEKLY",
  THREE_DAY: "THREE_DAY",
  DAILY: "DAILY",
};

// Display modes
export const DISPLAY_MODE = {
  TIME_AXIS: "TIME_AXIS",
  STACKED: "STACKED",
};

// Default operational days (Monday to Friday)
export const DEFAULT_OPERATIONAL_DAYS = [1, 2, 3, 4, 5];

// Calendar event types
export const ITEM_TYPES = {
  CALENDAR_EVENT: "CALENDAR_EVENT",
  CALENDAR_EVENT_ITEM: "CALENDAR_EVENT_ITEM",
};

// Time constants for full day events
export const TIME_CONSTANTS = {
  FULL_DAY_START: "00:00:00",
  FULL_DAY_END: "23:59:59",
};

// Layout constants
export const LAYOUT_CONSTANTS = {
  TIME_AXIS_PADDING: "64px",
};

// Content type filters
export const CONTENT_TYPES = {
  SCHOOL_CALENDAR: "SCHOOL_CALENDAR",
};

// ============================================================================
// DATE & TIME FORMATTING UTILITIES
// ============================================================================

/**
 * Formats a time range for display
 * @param {Object} params - Start and end time
 * @returns {string} - Formatted time range (e.g., "09:00 - 10:30 am")
 */
export const getFormattedTimeRange = ({ startTime, endTime }) => {
  const formattedStart = moment(startTime).format("hh:mm");
  const formattedEnd = moment(endTime).format("hh:mm a");

  return `${formattedStart} - ${formattedEnd}`;
};

/**
 * Formats a week duration for display
 * @param {Object} params - Start and end date
 * @returns {string} - Formatted duration string
 */
export const getWeekDurationFormat = ({ startDate, endDate }) => {
  const start = moment(startDate);
  const end = moment(endDate);

  if (start.isSame(end, "day")) {
    return start.format("DD MMM YYYY");
  }

  if (start.isSame(end, "month")) {
    return `${start.format("DD")} - ${end.format("DD MMM YYYY")}`;
  }

  if (start.isSame(end, "year")) {
    return `${start.format("DD MMM")} - ${end.format("DD MMM YYYY")}`;
  }

  return `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`;
};

export const getTimetablePlannerDisplaySettings = ({
  t,
  hideAssessments,
  curriculumType,
  isWidgetContext = false,
}) => {
  const assessmentTypeOptions = getAssessmentTypeDropdownMemoized({
    curriculumType,
    shouldIncludeQuickTasks: true,
    hideAssessments,
  });

  const contentTypeOptions = getItemTypeOptionsMemoized({
    assessmentTypeOptions,
  });

  const viewOptions = [
    {
      key: "view_timestamp_on_left_axis",
      label: t("course:view_timestamp_on_left_axis"),
      type: "checkbox",
    },
    {
      key: "show_calendar_events",
      label: t("course:show_calendar_events"),
      type: "checkbox",
    },
    {
      key: "view_subjects",
      label: t("course:view_subjects"),
      type: "checkbox",
    },
    {
      key: "view_locations",
      label: t("course:view_locations"),
      type: "checkbox",
    },
  ];

  if (isWidgetContext) {
    return viewOptions;
  }

  return [
    {
      isItemGroup: true,
      key: "view_options",
      label: t("course:view_options"),
      options: viewOptions,
    },
    {
      isItemGroup: true,
      key: "show_hide_sections_within_periods",
      label: t("course:show_hide_sections_within_periods"),
      options: _.map(contentTypeOptions, option => ({
        ...option,
        type: "checkbox",
      })),
    },
  ];
};

export const getUpdatedPeriods = ({
  allPeriods,
  displaySettings,
  getUserTime,
  isWidgetContext = false,
}) => {
  return _.map(allPeriods, period => {
    // filtering out display items
    const { items: allItems, startTime, endTime, date } = period;

    const filteredItems = _.filter(allItems, slotItem => {
      // Call the provided filter condition function
      const resourceType = _.get(slotItem, "itemType");
      const item = _.get(slotItem, "item");

      let type = "";
      let isFile = false;
      if (resourceType == "LEARNING_COURSE_FLOW") {
        const itemType = _.get(item, "itemType");

        if (itemType === "ASSESSMENT") {
          type = _.get(item, "item.assessmentType.value");
        } else {
          isFile = true;
          type = _.get(item, "item.type");
        }
      } else {
        type = _.get(item, "content.assessmentType.value");
      }

      if (_.has(displaySettings, type)) {
        return displaySettings[type];
      }

      if (isFile) {
        return displaySettings["REST_RESOURCES"];
      } else {
        return displaySettings["assessment"];
      }
    });

    // changing time and date based on timezone

    const originalStartTime = `${date} ${startTime}`;
    const userStartTime = getUserTime({ time: originalStartTime });

    const originalEndTime = `${date} ${endTime}`;
    const userEndTime = getUserTime({ time: originalEndTime });

    const formattedStartTime = moment(userStartTime).format("HH:mm:ss");
    const formattedEndTime = moment(userEndTime).format("HH:mm:ss");
    const formattedDate = moment(userStartTime).format(
      DATE_TIME_CONSTANTS.YYYY_MM_DD
    );

    return {
      ...period,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      date: formattedDate,
      allItems, // Preserve original/unfiltered items
      items: isWidgetContext ? allItems : filteredItems,
    };
  });
};

// ============================================================================
// OPERATIONAL DAYS UTILITIES
// ============================================================================

/**
 * Parse operational days from organization constants data
 * @param {Object} constantsData - Organization constants data from cache
 * @returns {number[]} Array of operational day indices (0=Sun, 1=Mon, etc.) or default [1,2,3,4,5]
 */
export const parseOperationalDays = constantsData => {
  const constants = _.get(constantsData, "node.globalConstants", []);

  const operationalDaysConstant = _.find(
    constants,
    ({ name }) => name === ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS
  );

  if (!operationalDaysConstant) {
    return DEFAULT_OPERATIONAL_DAYS;
  }

  const rawValue = _.get(operationalDaysConstant, "value");
  if (_.isNil(rawValue)) {
    return DEFAULT_OPERATIONAL_DAYS;
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    return _.isArray(parsedValue) && !_.isEmpty(parsedValue)
      ? parsedValue
      : DEFAULT_OPERATIONAL_DAYS;
  } catch (error) {
    console.warn("Failed to parse operational days:", error);
    return DEFAULT_OPERATIONAL_DAYS;
  }
};

// ============================================================================
// DATE RANGE CALCULATION UTILITIES
// ============================================================================

/**
 * Finds the first operational day from a given date
 * @private
 */
const getFirstOperationalDay = (date, operationalDays) => {
  const result = moment(date);
  while (!operationalDays.includes(result.day())) {
    result.add(1, "day");
  }
  return result;
};

/**
 * Finds the last operational day before or on a given date
 * @private
 */
const getLastOperationalDay = (date, operationalDays) => {
  const result = moment(date);
  while (!operationalDays.includes(result.day())) {
    result.subtract(1, "day");
  }
  return result;
};

/**
 * Gets the next operational date after the given date
 * First checks within the current dateRange, then calculates the next operational day
 * @param {Object} params - Parameters
 * @param {string|moment} params.currentDate - The current date
 * @param {Array} params.dateRange - Current visible date range
 * @param {Array} params.operationalDays - Array of operational day indices (0=Sun, 1=Mon, etc.)
 * @returns {moment} - The next operational date
 */
export const getNextOperationalDate = ({
  currentDate,
  dateRange = [],
  operationalDays = DEFAULT_OPERATIONAL_DAYS,
}) => {
  const currentDateMoment = moment(currentDate);

  // Try to find the next date in the current dateRange
  const nextDateInRange = _.find(dateRange, date =>
    moment(date).isAfter(currentDateMoment, "day")
  );

  if (nextDateInRange) {
    return moment(nextDateInRange);
  }

  // No next date in range - use getFirstOperationalDay to find next operational day
  const nextDay = moment(currentDate).add(1, "days");

  return getFirstOperationalDay(nextDay, operationalDays);
};

/**
 * Calculates weekly date range based on operational days
 * @private
 */
const calculateWeeklyRange = (date, operationalDays) => {
  const dayOfWeek = moment(date).day();
  const operationalStartDay = _.min(operationalDays);
  const operationalEndDay = _.max(operationalDays);

  const startDate = moment(date)
    .subtract(dayOfWeek - operationalStartDay, "days")
    .startOf("day");
  const endDate = moment(date)
    .add(operationalEndDay - dayOfWeek, "days")
    .endOf("day");

  return { startDate, endDate };
};

/**
 * Calculates three-day range for the calendar view
 * @private
 */
const calculateThreeDayRange = ({
  date,
  minDate,
  maxDate,
  operationalDays,
}) => {
  const operationalDaysInRange = [];
  const momentDate = moment(date);
  const momentMinDate = moment(minDate);
  const momentMaxDate = moment(maxDate, DATE_TIME_CONSTANTS.YYYY_MM_DD);

  // Determine starting loop date
  let loopDate;
  if (momentDate.isBetween(momentMinDate, momentMaxDate, null, "[]")) {
    loopDate = moment(momentDate);
  } else if (momentDate.isBefore(momentMinDate)) {
    loopDate = moment(momentMinDate);
  } else {
    loopDate = moment(momentMaxDate);
  }

  while (
    _.size(operationalDaysInRange) < 3 &&
    loopDate.isSameOrBefore(momentMaxDate)
  ) {
    if (operationalDays.includes(loopDate.day())) {
      operationalDaysInRange.push(
        loopDate.format(DATE_TIME_CONSTANTS.YYYY_MM_DD)
      );
    }
    loopDate.add(1, "day");
  }
  return operationalDaysInRange;
};

/**
 * Adjusts date range to fit within min/max boundaries
 * @private
 */
const adjustDateRangeForBoundaries = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  operationalDays,
  isDaily,
}) => {
  let adjustedStart = moment(startDate);
  let adjustedEnd = moment(endDate);

  if (moment(minDate).isAfter(adjustedStart)) {
    const updateMinDate = getFirstOperationalDay(
      moment(minDate),
      operationalDays
    );
    adjustedStart = updateMinDate;
    adjustedEnd = moment(updateMinDate).isSame(adjustedEnd, "week")
      ? adjustedEnd
      : moment(updateMinDate).add(
          _.max(operationalDays) - updateMinDate.day(),
          "days"
        );
    if (isDaily) adjustedEnd = moment(updateMinDate);
  }

  if (adjustedEnd.isAfter(moment(maxDate))) {
    const updateMaxDate = getLastOperationalDay(
      moment(maxDate),
      operationalDays
    );
    adjustedEnd = updateMaxDate;
    adjustedStart = moment(updateMaxDate).isSame(adjustedStart, "week")
      ? adjustedStart
      : moment(updateMaxDate).subtract(
          updateMaxDate.day() - _.min(operationalDays),
          "days"
        );
    if (isDaily) adjustedStart = moment(updateMaxDate);
  }

  return { startDate: adjustedStart, endDate: adjustedEnd };
};

/**
 * Generates an array of dates between start and end dates
 * @private
 */
const generateDateRangeArray = (startDate, endDate) => {
  const dateRange = [];
  const currentDate = moment(startDate);
  const weekEndDate = moment(endDate);

  while (currentDate.isSameOrBefore(weekEndDate)) {
    dateRange.push(currentDate.format(DATE_TIME_CONSTANTS.YYYY_MM_DD));
    currentDate.add(1, "day");
  }
  return dateRange;
};

/**
 * Gets operational week based on calendar view and operational days
 * @param {Object} params - Parameters for calculating operational week
 * @returns {Array} - Array of date strings in YYYY-MM-DD format
 */
export const getOperationalWeek = ({
  date,
  operationalDays = DEFAULT_OPERATIONAL_DAYS,
  minDate,
  maxDate,
  currentCalendarView = CALENDAR_VIEW.WEEKLY,
}) => {
  // Handle THREE_DAY view separately
  if (
    currentCalendarView !== CALENDAR_VIEW.WEEKLY &&
    currentCalendarView !== CALENDAR_VIEW.DAILY
  ) {
    return calculateThreeDayRange({ date, minDate, maxDate, operationalDays });
  }

  // Calculate initial date range
  let startDate, endDate;
  if (currentCalendarView === CALENDAR_VIEW.WEEKLY) {
    const range = calculateWeeklyRange(date, operationalDays);
    startDate = range.startDate;
    endDate = range.endDate;
  } else {
    startDate = moment(date).startOf("day");
    endDate = moment(date).endOf("day");
  }

  // Adjust for boundaries
  const isDaily = currentCalendarView === CALENDAR_VIEW.DAILY;
  const adjusted = adjustDateRangeForBoundaries({
    startDate,
    endDate,
    minDate,
    maxDate,
    operationalDays,
    isDaily,
  });

  const allDates = generateDateRangeArray(adjusted.startDate, adjusted.endDate);

  // Filter dates to only include operational days
  const filteredDates = _.filter(allDates, dateStr => {
    const dayOfWeek = moment(dateStr).day();
    return _.includes(operationalDays, dayOfWeek);
  });

  return filteredDates;
};

// ============================================================================
// CALENDAR EVENT UTILITIES
// ============================================================================

/**
 * Formats calendar events to match the period data structure
 * Handles both CalendarEvent and CalendarEventItem types
 * Separates full-day events from timed events
 *
 * @param {Array} calendarEvents - Raw calendar events from API
 * @returns {Array} - Formatted events with date/time information
 */
export const formatCalendarEvents = calendarEvents => {
  return _.map(calendarEvents, event => {
    const item = _.get(event, "item", {});
    const itemType = _.get(event, "itemType");

    const isCalendarEventType = itemType === ITEM_TYPES.CALENDAR_EVENT;

    // Extract start and end times based on event type
    const eventStartTime = isCalendarEventType
      ? _.get(item, "start")
      : event.startTime;

    const eventEndTime = isCalendarEventType
      ? _.get(item, "end")
      : event.endTime;

    // Only CalendarEventItem has isFullDayEvent field
    const isFullDayEvent =
      itemType === ITEM_TYPES.CALENDAR_EVENT_ITEM
        ? _.get(item, "isFullDayEvent", false)
        : false;

    const title =
      itemType === ITEM_TYPES.CALENDAR_EVENT_ITEM
        ? _.get(item, "label", "")
        : _.get(item, "title", "");

    const tag = _.get(item, "tag", {});

    // For full day events, use the start date directly and set times to full day bounds
    // For timed events, format the date and extract times from timestamps
    const date = isFullDayEvent
      ? eventStartTime
      : moment(eventStartTime).format(DATE_TIME_CONSTANTS.YYYY_MM_DD);

    const startTime = isFullDayEvent
      ? TIME_CONSTANTS.FULL_DAY_START
      : moment(eventStartTime).format(DATE_TIME_CONSTANTS.HH_mm_ss);

    const endTime = isFullDayEvent
      ? TIME_CONSTANTS.FULL_DAY_END
      : moment(eventEndTime).format(DATE_TIME_CONSTANTS.HH_mm_ss);

    return {
      ...event,
      date,
      startTime,
      endTime,
      itemType: ITEM_TYPES.CALENDAR_EVENT,
      timetableSlotId: `calendar-${event.id}`,
      title,
      calendarEventTag: tag,
      isFullDayEvent,
      isCalendarEvent: true, // Flag for z-index handling
    };
  });
};

/**
 * Separates formatted events into full-day and partial-day categories
 *
 * @param {Array} formattedEvents - Already formatted calendar events
 * @returns {Object} - Object with fullDayEvents and partialDayEvents arrays
 */
export const separateEventsByDuration = formattedEvents => ({
  fullDayEvents: _.filter(formattedEvents, { isFullDayEvent: true }),
  partialDayEvents: _.filter(formattedEvents, { isFullDayEvent: false }),
});

/**
 * Groups full-day events by date for rendering
 *
 * @param {Array} fullDayEvents - Array of full-day events
 * @returns {Object} - Events grouped by date
 */
export const groupEventsByDate = fullDayEvents => {
  return _.groupBy(fullDayEvents, "date");
};

// Break period item type constant

export const extractBreakPeriods = routineDates => {
  if (_.isEmpty(routineDates)) {
    return [];
  }

  return _.flatMap(routineDates, routineDate => {
    const date = _.get(routineDate, "date");
    const bellSchedulePeriods = _.get(
      routineDate,
      "bellSchedule.periodsEdge.periods",
      []
    );
    const breakPeriods = _.filter(
      bellSchedulePeriods,
      ({ period }) => _.get(period, "type") === BREAK_ITEM_TYPE
    );
    return _.map(breakPeriods, bellSchedulePeriod => {
      const { period, startTime, endTime } = bellSchedulePeriod;
      const periodId = _.get(period, "id", "");

      return {
        date,
        startTime,
        endTime,
        period,
        itemType: BREAK_ITEM_TYPE,
        timetableSlotId: `break-${periodId}-${date}`,
        isBreakPeriod: true,
      };
    });
  });
};

export const teacherDropdownOptions = ({ teachersList }) =>
  _.map(teachersList, ({ node }) => {
    const { id, profileImage, email } = node;
    const fullName = getFormattedUserName({ user: node, type: "staff" });

    return {
      icon: (
        <Avatar
          dsVersion="2.0"
          id={id}
          name={fullName}
          src={profileImage}
          shade="light"
        />
      ),
      key: id,
      label: fullName,
      subText: email,
    };
  });

// ============================================================================
// GRAPHQL QUERY VARIABLE BUILDERS
// ============================================================================

export const getClassTimetablePlannerVariables = ({
  classIds,
  isTimetableTypeClass,
  academicYearId,
  weekStartDate,
  weekEndDate,
  curriculumProgramId,
  dateRange,
  selectedCourseId,
  isTimetableTypeStudent,
  userId,
}) => {
  return {
    variables: {
      ids: isTimetableTypeStudent ? selectedCourseId : classIds,
      periodsFilters: {
        startDate: weekStartDate,
        endDate: weekEndDate,
        academicYearId,
        curriculumProgramIds: [curriculumProgramId],
        isAllDayPeriods: false,
        onlySelectedSlots: true,
      },
      permissionFilters: {
        academicYearId,
      },
      termFilters: {
        types: ["REPORTING", "EXAM"],
      },
      attendanceFilters: {
        academicYearId,
      },
      ...(isTimetableTypeStudent
        ? {
            itemFilter: {
              studentIds: [userId],
            },
          }
        : {}),
    },
    skip: _.isEmpty(dateRange) || !isTimetableTypeClass,
  };
};

export const getRoutineDetailsVariables = ({
  isTimetableTypeTeacher,
  organizationId,
  academicYearId,
  primaryGradeId,
  weekStartDate,
  weekEndDate,
  curriculumProgramId,
  dateRange,
  isTimetableTypeStudent,
  studentGradeId,
  studentId,
}) => {
  return {
    variables: {
      id: organizationId,
      curriculumProgramIds: [curriculumProgramId],
      routineFilters: {
        academicYearId,
        startDate: weekStartDate,
        endDate: weekEndDate,
        gradeIds:
          isTimetableTypeStudent || studentId
            ? [studentGradeId]
            : primaryGradeId
            ? [primaryGradeId]
            : [],
      },
      routineDatesFilters: {
        academicYearId,
        startDate: weekStartDate,
        endDate: weekEndDate,
      },
      fetchBellSchedule: isTimetableTypeStudent ? true : false,
    },
    skip: isTimetableTypeTeacher || _.isEmpty(dateRange) || !organizationId,
  };
};

export const getCalendarEventsVariables = ({
  weekStartDate,
  weekEndDate,
  userId,
  dateRange,
  curriculumProgramId,
  curriculumType,
  academicYearId,
  isTimetableTypeStudent,
}) => {
  return {
    variables: {
      id: userId,
      type: isTimetableTypeStudent ? "STUDENT" : "STAFF",
      filters: {
        startDate: weekStartDate
          ? moment(weekStartDate).startOf("day").toISOString()
          : undefined,
        endDate: weekEndDate
          ? moment(weekEndDate).endOf("day").toISOString()
          : undefined,
        contentTypes: [CONTENT_TYPES.SCHOOL_CALENDAR],
        curriculumProgramId,
        curriculumType,
        academicYearId,
      },
    },
    skip: _.isEmpty(dateRange),
  };
};

export const getTeacherTimetableVariables = ({
  weekStartDate,
  weekEndDate,
  academicYearId,
  dateRange,
  curriculumProgramId,
  selectedStaffId = "",
  learningCourseId,
  selectedStudentId = "",
  isTimetableTypeStudent,
  isTimetableTypeClass,
}) => {
  return {
    variables: {
      id: isTimetableTypeStudent ? selectedStudentId : selectedStaffId,
      type: isTimetableTypeStudent ? "STUDENT" : "STAFF",
      periodsFilters: {
        startDate: weekStartDate,
        endDate: weekEndDate,
        academicYearId,
        isAllDayPeriods: false,
        onlySelectedSlots: true,
        ...(learningCourseId ? { learningCourseIds: [learningCourseId] } : {}),
      },
      permissionFilters: {
        academicYearId,
      },
      termFilters: {
        types: ["REPORTING", "EXAM"],
      },
      attendanceFilters: {
        academicYearId,
      },
      ...(isTimetableTypeStudent
        ? { curriculumProgramIds: [curriculumProgramId] }
        : {}),
    },
    skip:
      isTimetableTypeClass ||
      (_.isEmpty(selectedStaffId) && !isTimetableTypeStudent) ||
      (_.isEmpty(selectedStudentId) && isTimetableTypeStudent) ||
      _.isEmpty(dateRange),
  };
};

export const getOrganizationTeachersVariables = ({
  organizationId,
  curriculumProgramId,
  academicYearId,
  isTimetableTypeTeacher,
  isCombineCurriculumEnabled,
  organizationCurriculumProgramIds,
}) => {
  const curriculumProgramIds = isCombineCurriculumEnabled
    ? organizationCurriculumProgramIds
    : curriculumProgramId;

  const isCurriculumProgramMissing = isCombineCurriculumEnabled
    ? _.isEmpty(organizationCurriculumProgramIds)
    : _.isNil(curriculumProgramId);

  return {
    variables: {
      organizationId,
      archivalState: "ACTIVE",
      curriculumProgramIds,
      academicYearIds: academicYearId,
      searchText: "",
    },
    skip: !isTimetableTypeTeacher || isCurriculumProgramMissing,
  };
};

export const getUserCoursesVariables = ({
  userId,
  curriculumProgramId,
  academicYearId,
  isUserTeacher,
  isTimetableTypeClass,
  isTimetableTypeTeacher,
}) => {
  return {
    variables: {
      id: userId,
      filters: {
        curriculumProgramIds: [curriculumProgramId],
        archivalState: "ACTIVE",
        isDemo: false,
      },
      courseGradeFilters: {
        academicYearId,
      },
    },
    skip: isUserTeacher && (isTimetableTypeClass || isTimetableTypeTeacher),
  };
};

// ============================================================================
// SLOT ITEM MENU OPTIONS
// ============================================================================

/**
 * Menu action keys for slot items
 */
export const SLOT_ITEM_ACTIONS = {
  DOWNLOAD: "DOWNLOAD",
  EDIT: "EDIT",
  ASSIGN_TO_STUDENTS: "ASSIGN_TO_STUDENTS",
  SHARE_SETTINGS: "SHARE_SETTINGS",
  REPEAT: "REPEAT",
  MOVE: "MOVE",
  CHANGE_TASK: "CHANGE_TASK",
  DELETE: "DELETE",
};

export const SLOT_ACTIONS = {
  REPEAT: "REPEAT",
  MOVE: "MOVE",
  CLEAR_CARD: "CLEAR_CARD",
};

// Assessment types that cannot change task type
const ASSESSMENT_TYPES_NO_CHANGE_TASK = ["worksheet", "ai_tutor"];

/**
 * Get disabled tooltip for menu items with precedence
 * @param {Object} params - Parameters
 * @returns {string} Tooltip text
 */
const getDisabledTooltipWithPrecedence = ({
  t,
  hasStudents,
  isEditAllowed,
  isSameCurriculum,
  curriculumLabel,
  isGradingPeriodUnlocked,
  isTermExistsForCurrentLearningCourse,
  assessmentLabel,
  actionLabel,
  entityType = "sharing", // 'sharing' or 'assigning'
}) => {
  if (!isSameCurriculum) {
    return t("course:period_belong_to_curriculum_label", {
      label: curriculumLabel,
    });
  }
  if (entityType === "assigning") {
    if (!isGradingPeriodUnlocked) {
      return t("course:you_cannot_with_label_since_locked", {
        actionLabel,
      });
    }
    if (!isTermExistsForCurrentLearningCourse) {
      return t(
        "classRoom:assign_to_students_no_grading_periods_tooltip_with_label_plural",
        {
          label: assessmentLabel,
        }
      );
    }
  }
  if (!hasStudents) {
    return t("common:no_students_enrolled");
  }
  if (!isEditAllowed) {
    return t("course:no_creation_permission_tooltip");
  }
  return "";
};

const getDisabledTooltipForUpdations = ({
  t,
  isEditAllowed,
  isSameCurriculum,
  isUpdateActionsLocked = false,
  isGradingPeriodUnlocked = true,
  curriculumLabel,
  entityLabel,
  actionLabel,
  defaultTooltip = "",
}) => {
  if (!isSameCurriculum) {
    return t("course:period_belong_to_curriculum_label", {
      label: curriculumLabel,
    });
  }

  if (isUpdateActionsLocked) {
    return t("course:locked_item_edit_restriction_tooltip", {
      entity: entityLabel,
    });
  }

  if (!isGradingPeriodUnlocked) {
    return t("course:you_cannot_with_label_since_locked", {
      actionLabel,
    });
  }

  if (!isEditAllowed) {
    return t("course:no_creation_permission_tooltip");
  }

  return defaultTooltip;
};

/**
 * Get menu options for a slot item based on its type
 * @param {Object} params - Parameters
 * @param {Object} params.t - Translation function
 * @param {string} params.itemType - The slot item type (LEARNING_COURSE_FLOW, ASSIGNMENT, FILE)
 * @param {string} params.type - The effective/underlying type (ASSESSMENT, NOTE, FILE, LINK, etc.)
 * @param {boolean} params.isPrintEnabled - Whether print/download is enabled (ACL)
 * @param {boolean} params.isProSchool - Whether it's a pro school
 * @param {boolean} params.hasStudents - Whether classes have students
 * @param {boolean} params.isEditAllowed - Edit permission from learning course flow
 * @param {boolean} params.isSameCurriculum - Whether the slot is in the same curriculum
 * @param {boolean} params.isSharingAllowed - Sharing permission from learning course flow
 * @param {boolean} params.isLocked - Whether the item is locked
 * @param {boolean} params.isGradingPeriodUnlocked - Whether grading period is unlocked
 * @param {boolean} params.isTermExistsForCurrentLearningCourse - Whether term exists
 * @param {string} params.curriculumType - Curriculum type (IB_PYP, etc.)
 * @param {string} params.assessmentType - Assessment type (le, worksheet, ai_tutor, etc.)
 * @param {string} params.assessmentLabel - Assessment type label for tooltips
 * @param {boolean} params.curriculumHasAssessments - Whether curriculum allows assessments
 * @returns {Array} Array of menu options
 */
export const getSlotItemMenuOptions = ({
  t,
  type,
  slotItem,
  course,
  isEditAllowed = true,
  isSameCurriculum = true,
  isSharingAllowed = true,
  isLocked = false,
  academicTerm,
  isTermExistsForCurrentLearningCourse = true,
  assessmentType = "le",
  assessmentLabel = "",
  isShared = false,
  isPreviousAcademicYearSelected = false,
}) => {
  const isPrintEnabled = !ACLStore.can(
    "FeatureFlag:PreventPrintAndScreenshots"
  );
  const isProSchool = getIsProSchool();

  const curriculumType = _.get(course, "curriculumProgram.type", "");
  const curriculumLabel = _.get(course, "curriculumProgram.label", "");

  // Check if curriculum supports assessments
  const curriculumHasAssessments =
    curriculumType !== CURRICULUM_TYPE_PYP ||
    ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);

  // Calculate grading period unlock status
  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
    academicTerm,
    assessmentType
  );

  const innerSlotItemType = _.get(slotItem, "item.itemType", "");
  const hasStudents = _.get(course, "students.totalCount", 0) > 0;

  const isAssessment = type === "ASSESSMENT";
  const isNote = type === "NOTE";
  const isLink = type === "LINK";
  const isAttachment = innerSlotItemType === "FILE";
  const isDownloadable = isAttachment && !isNote && !isLink;

  // Determine effective permissions based on item type
  // Check if update actions are locked
  const isUpdateActionsLocked = isLocked;

  // Check if this assessment type can change task
  const canChangeTaskType = !_.includes(
    ASSESSMENT_TYPES_NO_CHANGE_TASK,
    assessmentType
  );

  const options = [
    // Download - only for downloadable attachments (not notes/links)
    {
      key: SLOT_ITEM_ACTIONS.DOWNLOAD,
      label: t("common:download"),
      icon: <DownloadOutlined />,
      isVisible: isDownloadable && isPrintEnabled,
    },
    {
      key: SLOT_ITEM_ACTIONS.EDIT,
      label: t("common:edit"),
      icon: <PencilLineOutlined />,
      isVisible: (isNote || isLink || isAssessment) && isEditAllowed,
      disabled:
        !isSameCurriculum || isUpdateActionsLocked || !isGradingPeriodUnlocked,
      disabledTooltip: getDisabledTooltipForUpdations({
        t,
        isEditAllowed,
        isSameCurriculum,
        isUpdateActionsLocked,
        isGradingPeriodUnlocked,
        curriculumLabel,
        entityLabel: isAssessment
          ? assessmentLabel
          : isNote
          ? t("common:note")
          : t("common:link"),
        actionLabel: t("common:edit"),
      }),
      suffix: isUpdateActionsLocked ? (
        <LockFilled variant="subtle" size="xxx-small" />
      ) : null,
    },
    // Assign to students - for assessments (not for pro schools)
    {
      key: SLOT_ITEM_ACTIONS.ASSIGN_TO_STUDENTS,
      label: isShared
        ? t("classRoom:edit_assign_settings")
        : t("common:assign"),
      icon: <UserTickOutlined />,
      isVisible:
        isAssessment &&
        !isProSchool &&
        isSharingAllowed &&
        !isPreviousAcademicYearSelected,
      disabled:
        !hasStudents ||
        !isSameCurriculum ||
        !isGradingPeriodUnlocked ||
        !isTermExistsForCurrentLearningCourse,
      disabledTooltip: getDisabledTooltipWithPrecedence({
        t,
        hasStudents,
        isEditAllowed,
        isSameCurriculum,
        isGradingPeriodUnlocked,
        isTermExistsForCurrentLearningCourse,
        assessmentLabel,
        curriculumLabel,
        actionLabel: isShared
          ? t("classRoom:edit_assign_settings")
          : t("common:assign"),
        entityType: "assigning",
      }),
    },
    // Share settings - for attachments
    {
      key: SLOT_ITEM_ACTIONS.SHARE_SETTINGS,
      label: t("course:edit_share_settings"),
      icon: <ShareCurveOutlined />,
      isVisible:
        isAttachment && isSharingAllowed && !isPreviousAcademicYearSelected,
      disabled: !hasStudents || !isSameCurriculum,
      disabledTooltip: getDisabledTooltipWithPrecedence({
        t,
        hasStudents,
        isEditAllowed,
        isSameCurriculum,
        curriculumLabel,
        entityType: "sharing",
      }),
    },
    // Move to
    {
      key: SLOT_ITEM_ACTIONS.MOVE,
      label: isAttachment
        ? t("classPlanner:move_resource_to")
        : t("classPlanner:move_task_to"),
      icon: <SwitchArrowsOutlined />,
      disabled: !isEditAllowed || !isSameCurriculum,
      disabledTooltip: getDisabledTooltipForUpdations({
        t,
        isEditAllowed,
        isSameCurriculum,
        curriculumLabel,
      }),
      isVisible: isEditAllowed,
    },
    // Copy to (Repeat)
    {
      key: SLOT_ITEM_ACTIONS.REPEAT,
      label: isAttachment
        ? t("classPlanner:copy_resource_to")
        : t("classPlanner:copy_task_to"),
      icon: <CopyOutlined />,
      disabled: !isEditAllowed || !isSameCurriculum,
      disabledTooltip: getDisabledTooltipForUpdations({
        t,
        isEditAllowed,
        isSameCurriculum,
        curriculumLabel,
      }),
      isVisible: isEditAllowed,
    },
    // Change task type - for assessments (only if curriculum supports assessments)
    {
      key: SLOT_ITEM_ACTIONS.CHANGE_TASK,
      label: t("common:change_task_type"),
      icon: <ReloadArrowOutlined />,
      isVisible: isAssessment && curriculumHasAssessments && isEditAllowed,
      disabled:
        isUpdateActionsLocked ||
        !canChangeTaskType ||
        !isEditAllowed ||
        !isSameCurriculum ||
        !isGradingPeriodUnlocked,
      suffix: isUpdateActionsLocked ? (
        <LockFilled variant="subtle" size="xxx-small" />
      ) : null,
      disabledTooltip: getDisabledTooltipForUpdations({
        t,
        isEditAllowed,
        isSameCurriculum,
        isUpdateActionsLocked,
        isGradingPeriodUnlocked,
        curriculumLabel,
        entityLabel: assessmentLabel,
        actionLabel: t("common:change_task_type"),
        defaultTooltip: t(
          "classRoom:change_task_type_dropdown_disabled_tooltip"
        ),
      }),
    },
    // Remove/Delete
    {
      key: SLOT_ITEM_ACTIONS.DELETE,
      label: t("common:remove"),
      icon: <DeleteOutlined />,
      isVisible: isEditAllowed,
      disabled: isUpdateActionsLocked || !isSameCurriculum,
      suffix: isUpdateActionsLocked ? (
        <LockFilled variant="subtle" size="xxx-small" />
      ) : null,
      disabledTooltip: getDisabledTooltipForUpdations({
        t,
        isEditAllowed,
        isSameCurriculum,
        curriculumLabel,
        entityLabel: isAttachment
          ? t("common:resource_label")
          : assessmentLabel,
      }),
      isDestructive: true,
    },
  ];

  // Filter to only visible options
  return _.filter(options, "isVisible");
};

export const getSlotCardMenuOptions = ({ t, noItemsAdded }) => {
  return [
    {
      key: SLOT_ACTIONS.MOVE,
      label: t("classPlanner:section_move_content_to"),
      icon: <SwitchArrowsOutlined variant={"subtle"} size={"xx-small"} />,
      disabled: noItemsAdded,
      disabledTooltip: t("classPlanner:disabled_tooltip_message_period"),
    },
    {
      key: SLOT_ACTIONS.REPEAT,
      label: t("classPlanner:copy_content_to"),
      icon: <CopyOutlined variant={"subtle"} size={"xx-small"} />,
      disabled: noItemsAdded,
      disabledTooltip: t("classPlanner:disabled_tooltip_message_period"),
    },
    {
      key: SLOT_ACTIONS.CLEAR_CARD,
      label: t("classPlanner:clear_content_from_period"),
      icon: <DeleteOutlined variant={"critical"} size={"xx-small"} />,
      isDestructive: true,
      disabled: noItemsAdded,
      disabledTooltip: t("classPlanner:disabled_tooltip_message_period"),
    },
  ];
};

export const getCopyContentModalText = ({
  t,
  mode,
  title,
  isCopyContentToDateRangeEnabled,
  attachmentType,
}) => {
  // Title
  let titleLocale = "classPlanner:move_resource_to_title";
  if (mode === SLOT_ACTIONS.REPEAT) {
    titleLocale = isCopyContentToDateRangeEnabled
      ? "classPlanner:copy_resource_to_multiple_blocks"
      : "classPlanner:copy_resource_to_title";
  }
  const formattedLabel =
    attachmentType === "NOTE"
      ? HtmlParser(
          convertHtmlToTextWithSpanConversion({
            title: getStyleStrippedText({
              text: title,
              type: "NOTES_TITLE",
            }),
          })
        )
      : title;
  const modalTitle = t(titleLocale, { label: formattedLabel });

  // Subtitle
  let subtitleLocale = "classPlanner:move_task_subtitle";
  if (mode === SLOT_ACTIONS.REPEAT) {
    subtitleLocale = isCopyContentToDateRangeEnabled
      ? null
      : "classPlanner:repeat_task_subtitle";
  }
  const modalSubtitle = subtitleLocale
    ? t(subtitleLocale, { label: formattedLabel })
    : "";

  return { title: modalTitle, subtitle: modalSubtitle };
};

//Get background color for image container based on file type and MIME type
export const getBackgroundColorForImageContainer = ({ type, mimeType }) => {
  switch (type) {
    case "NOTE":
      return colors.surfaceWarning;
    case "LINK":
      return colors.surfaceLink;
    case "AUDIO":
      return colors.decorativeSurfaceTwoL;
    case "VIDEO":
      return colors.surfaceError;
    case "IMAGE":
      return colors.decorativeSurfaceTwoL;
    case "FILE":
      switch (mimeType) {
        case "application/pdf":
          return colors.surfaceError;
        case "application/zip":
          return colors.surfaceWarning;
        case "application/vnd.google-apps.document":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        case "application/msword":
        case "application/x-iwork-pages-sffpages":
        case "application/vnd.apple.pages":
          return colors.surfaceLink;
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        case "application/vnd.google-apps.spreadsheet":
        case "application/vnd.ms-excel":
        case "application/x-iwork-numbers-sffnumbers":
        case "application/vnd.apple.numbers":
        case "text/csv":
          return colors.surfaceSuccess;
        case "application/vnd.google-apps.presentation":
        case "application/vnd.ms-powerpoint":
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        case "application/x-iwork-keynote-sffkey":
        case "application/vnd.apple.keynote":
          return colors.surfaceWarning;
        default:
          return colors.surfaceSubtle;
      }
    default:
      return colors.surfaceSubtle;
  }
};

export const getRethumb = imageUrl => {
  return imageUrl
    ? getRethumbUrl({
        width: 64,
        height: 64,
        imageUrl,
        fitIn: true,
      })
    : "";
};

export const getIcon = getMemoizedLangFunction(
  ({
    orgResource,
    displaySettings,
    classes,
    isCompact,
    size = "medium",
    unitPlanSize = "small",
    disabled = false,
  }) => {
    const { resourceType, item, attachment } = orgResource;
    const showThumbnailImage = _.get(displaySettings, "thumbnail_image", true);

    switch (resourceType) {
      case "ASSESSMENT":
      case "ASSIGNMENT": {
        const itemData =
          resourceType == "ASSESSMENT" ? item : _.get(item, "content", {});
        const assessmentType = _.get(itemData, "assessmentType.value");
        const imageUrl = _.get(itemData, "image.value", null);

        if (imageUrl && showThumbnailImage) {
          return (
            <div
              className={classes?.imageContainer}
              style={{
                backgroundImage: `url(${getRethumb(imageUrl)})`,
                backgroundSize: "cover",
                backgroundColor: colors["decorativeSurfaceThreeL"],
              }}
            />
          );
        } else {
          return (
            <AssessmentIcon
              assessmentType={assessmentType}
              className={classNames(classes?.noShrink, {
                [classes?.compactIcon]: isCompact,
              })}
              size={size}
              disabled={disabled}
            />
          );
        }
      }

      case "ORGANIZATION_RESOURCE":
      case "FILE": {
        const attachmentData =
          resourceType == "FILE" ? attachment : _.get(item, "attachment", {});
        const attachmentDataType = attachmentData?.type;
        const attachmentDataMimeType = attachmentData?.mimeType;
        const attachmentDataThumbUrl = attachmentData?.thumbUrl;

        if (attachmentDataType) {
          const hasThumbnaiImage =
            attachmentDataType === "IMAGE" ||
            attachmentDataMimeType === "application/pdf" ||
            (attachmentDataType === "LINK" && attachmentDataThumbUrl);
          const icon = getThumbnailIcon({ ...attachmentData, disabled });

          const { thumbUrl } = hasThumbnaiImage
            ? getThumbUrl({
                attachment: attachmentData,
              })
            : {};

          const containerStyle =
            hasThumbnaiImage && showThumbnailImage
              ? {
                  border: "none",
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }
              : {
                  border: "none",
                  width: "20px",
                  height: "20px",
                  borderRadius: "8px",
                };

          return (
            <div
              style={{
                backgroundColor: disabled
                  ? colors.surfaceSubtle
                  : getBackgroundColorForImageContainer({
                      type: attachmentDataType,
                      mimeType: attachmentData.mimeType,
                    }),
              }}
              className={classes?.iconContainer}
            >
              <ResourceImagePlaceHolder
                type={attachmentDataType}
                icon={!hasThumbnaiImage || !showThumbnailImage ? icon : null}
                imageUrl={
                  hasThumbnaiImage && showThumbnailImage ? thumbUrl : null
                }
                containerStyle={containerStyle}
              />
            </div>
          );
        }
      }
    }

    return null;
  },
  ({ orgResource, displaySettings, disabled = false, size }) => {
    const resourceData = _.pick(orgResource, [
      "resourceType",
      "item",
      "attachment",
      "isRepeated",
    ]);
    const baseKey = displaySettings?.thumbnail_image
      ? `${JSON.stringify(resourceData)}_${displaySettings.thumbnail_image}`
      : JSON.stringify(resourceData);

    return `${baseKey}_${disabled}_${size}`;
  }
);

const TIMETABLE_WIDGET_SCROLL_CONTAINER_ID =
  "timetable-widget-scroll-container";

/**
 * Finds the timetableSlotId of the period whose startTime is
 * just before (or equal to) the current time for today's date,
 * then scrolls the widget's viewContainer to bring that element into view.
 * Only works in widget context where the viewContainer has the known id.
 * @param {Array} periods - Array of period objects with date, startTime, and timetableSlotId
 */
export const scrollToCurrentTimeSlot = ({ periods }) => {
  const currentTime = moment().format("HH:mm:ss");

  // Filter periods that have started but not yet ended
  const activePeriods = _.filter(
    periods,
    period =>
      _.get(period, "startTime") <= currentTime &&
      _.get(period, "endTime") > currentTime
  );

  // From active periods, pick the one with the latest startTime.
  // If multiple share the same latest startTime, pick the first one among them.
  const latestStartTime = _.get(_.last(activePeriods), "startTime");
  const targetPeriod = _.find(
    activePeriods,
    period => _.get(period, "startTime") === latestStartTime
  );

  const targetSlotId = _.get(targetPeriod, "timetableSlotId");

  if (!targetSlotId) return;

  // Allow a brief render cycle so the DOM element is available
  requestAnimationFrame(() => {
    const element = document.getElementById(`eventCard-${targetSlotId}`);
    const scrollContainer = document.getElementById(
      TIMETABLE_WIDGET_SCROLL_CONTAINER_ID
    );

    if (!element || !scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const offsetTop =
      elementRect.top - containerRect.top + scrollContainer.scrollTop;

    scrollContainer.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  });
};

export const filterDropdownOptions = ({ options, optionsToRemove = [] }) => {
  if (!_.isArray(options)) return [];
  if (_.isEmpty(optionsToRemove)) return options;

  const optionKeysToRemove = new Set(optionsToRemove);

  return _.reduce(
    options,
    (filteredOptions, option) => {
      const optionKey = _.get(option, "key");

      if (optionKeysToRemove.has(optionKey)) {
        return filteredOptions;
      }

      const nestedOptions = _.get(option, "options");
      const nextOption = _.isArray(nestedOptions)
        ? {
            ...option,
            options: filterDropdownOptions({
              options: nestedOptions,
              optionsToRemove,
            }),
          }
        : option;

      filteredOptions.push(nextOption);
      return filteredOptions;
    },
    []
  );
};
